/**
 * Index page, which servers as landingpage for the '/' route.
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module pages/index.jsx
 * @class IndexPage
 * @since 0.4.13
 */

import React, {useEffect} from 'react';
import {useStore} from 'effector-react';
import {Button} from '@rmwc/button';
import {Link} from 'gatsby';
import langNavigate from '../lib/helpers/lang-navigate';
import Seo from '../components/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LanguageSelectionScreen from '../components/language-selection-screen';
import WalletSelectionScreen from '../components/wallet-selection-screen';
import MigrationProgress from '../components/migration-progress';
import translations from '../lib/translations';
import {getLanguageUrl} from '../lib/helpers/language-url';
import isMobile from '../lib/helpers/detect-mobile';
import languageSelection from '../globals/language-selection';
import metamask from '../globals/metamask';
import WalletLogoutForm from '../components/wallet-logout-form';

// images
import imgHeader from '../images/header.svg';
import imgWallet from '../images/wallet.svg';
import imgMigration from '../images/gen3.svg';
import imgMN from '../images/masternode.svg';
import imgStaking from '../images/staking.svg';
import imgTreasury from '../images/gov.svg';
import imgEBI from '../images/ebi.svg';
import imgAlerts from '../images/email-alerts.svg';
import imgRewardCalculator from '../images/calculator.svg';

const IndexPage = props => {
    let langSelection, walletLogoutForm, walletSelection;
    const
        languageSelectionVisible = useStore(languageSelection.$visible),
        walletSelectionVisible = useStore(metamask.$walletSelectionVisible),
        walletLogoutFormVisible = useStore(metamask.$logoutFormVisible),
        metamaskInstalled = useStore(metamask.$installed),
        langInfo = props.pageContext.langInfo,
        translate = translations.init(props),
        isMetamaskMobile = metamaskInstalled && isMobile,
        BLOCKS = [
            {
                image: imgWallet,
                title: 'MY_ENERGI_WALLET',
                text: 'index-description-myenergiwallet',
                url: '/energi-wallet'
            },
            {
                image: imgAlerts,
                title: 'ALERTS',
                text: 'index-description-alerts',
                url: '/alerts'
            },
            {
                image: imgMN,
                title: 'MASTERNODES',
                text: 'index-description-masternodes',
                url: '/masternodes/listing'
            },
            {
                image: imgStaking,
                title: 'STAKING',
                text: 'index-description-staking',
                url: '/staking/analytics'
            },
            {
                image: imgRewardCalculator,
                title: 'REWARD_CALCULATOR',
                text: 'reward-calculator',
                url: '/reward-calculator',
            },
            {
                image: imgTreasury,
                title: 'TREASURY_BUDGET',
                text: 'index-description-treasurybudget',
                url: '/treasury-budget'
            },
            {
                image: imgMigration,
                title: 'MIGRATE_GEN3',
                text: 'index-description-migrategen3',
                url: '/coin-migration'
            },
            {
                image: imgEBI,
                title: 'EBI_RECOVERY',
                text: 'index-description-ebirecovery',
            },
        ];

    useEffect(() => {
        languageSelection.resetScroll();
    });

    const ListElement = (index, image, title, text, url) => {
        if (url) {
            return (
                <div className="block" key={index}>
                    <Link to={getLanguageUrl(langInfo, url)}>
                        <img
                            alt=""
                            src={image} />
                        <h5>{translate.t(title, true)}</h5>
                        <div dangerouslySetInnerHTML={{__html: translate.md(text)}} />
                    </Link>
                </div>
            );
        }

        return (
            <div className="block block-static" key={index}>
                <img
                    alt=""
                    src={image} />
                <h5>{translate.t(title, true)}</h5>
                <div className="coming-soon">{translate.t('COMING_SOON')}!</div>
                <div dangerouslySetInnerHTML={{__html: translate.md(text)}} />
            </div>
        );
    };

    const getAllElements = () => {
        return BLOCKS.map((blockData, index) => ListElement(index, blockData.image, blockData.title, blockData.text, blockData.url));
    };

    if (languageSelectionVisible) {
        langSelection = <LanguageSelectionScreen {...props} />;
    }

    if (walletSelectionVisible && !isMetamaskMobile) {
        walletSelection = <WalletSelectionScreen {...props} />;
    }

    if (walletLogoutFormVisible && (!isMobile || isMetamaskMobile)) {
        walletLogoutForm = <WalletLogoutForm {...props} />;
    }

    return (
        <div className="pure-g">
            {langSelection}
            {walletSelection}
            <Seo lang={langInfo.lang} title="Welcome" />
            <Header toggleIcon={false} {...props.pageContext} />
            {walletLogoutForm}
            <section className="homepage">
                <div className="homepage-top">
                    <img
                        alt=""
                        src={imgHeader} />
                    <div className="position-center">
                        <h5>{translate.t('WELCOME_TO_NEXUS', true)}</h5>
                        <p dangerouslySetInnerHTML={{__html: translate.md('index-intro')}} />
                        <Button
                            className="buttonGreen"
                            label={translate.t('GET_STARTED')}
                            onClick={() => langNavigate(langInfo, '/coin-migration')}
                            raised />
                    </div>
                </div>

                <div className="homepage-body">
                    <div className="homepage-body-inner">
                        {getAllElements()}
                    </div>
                </div>
                <MigrationProgress {...props.pageContext} />
                <Footer {...props} />
            </section>
        </div>
    );
};

export default IndexPage;
